import React, { useCallback, useEffect, useRef, useState } from 'react';
import { recordScore, getProgress, setLevel, unlockNext, getHighScore } from '../gamesLib';
import { sfx } from '../gameSound';

interface Props {
  onExit: () => void;
  challengeScore?: number | null;
}

const W = 360;
const H = 420;
const PADDLE_W = 72;
const PADDLE_H = 12;
const BALL_R = 6;
const BRICK_ROWS_BY_LEVEL = [4, 5, 6, 7, 8];
const BRICK_COLS = 8;
const BRICK_H = 16;
const BRICK_GAP = 4;
const BALL_SPEED_BY_LEVEL = [3.2, 3.7, 4.2, 4.8, 5.4];
const ROW_COLORS = ['#ef5350', '#ff7043', '#ffa726', '#ffca28', '#66bb6a', '#26c6da', '#42a5f5', '#7e57c2'];

interface Brick { x: number; y: number; w: number; h: number; alive: boolean; color: string; }

const GameBreakout: React.FC<Props> = ({ onExit, challengeScore }) => {
  const prog = getProgress('breakout');
  const [level, setLvl] = useState(prog.level || 1);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [best] = useState(getHighScore('breakout'));
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const [cleared, setCleared] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const savedRef = useRef(false);

  // Mutable game state lives in a ref so the rAF loop reads fresh values.
  const g = useRef({
    paddleX: (W - PADDLE_W) / 2,
    ball: { x: W / 2, y: H - 60, vx: 3, vy: -3 },
    bricks: [] as Brick[],
    score: 0,
    lives: 3,
    running: false,
    speed: 3.2,
  });

  const buildBricks = useCallback((lvl: number) => {
    const rows = BRICK_ROWS_BY_LEVEL[Math.min(lvl - 1, BRICK_ROWS_BY_LEVEL.length - 1)];
    const brickW = (W - BRICK_GAP * (BRICK_COLS + 1)) / BRICK_COLS;
    const bricks: Brick[] = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < BRICK_COLS; c++) {
        bricks.push({
          x: BRICK_GAP + c * (brickW + BRICK_GAP),
          y: 40 + r * (BRICK_H + BRICK_GAP),
          w: brickW, h: BRICK_H, alive: true,
          color: ROW_COLORS[r % ROW_COLORS.length],
        });
      }
    }
    return bricks;
  }, []);

  const resetBall = useCallback((speed: number) => {
    const angle = (Math.random() * 0.5 - 0.25); // mostly upward
    g.current.ball = { x: W / 2, y: H - 60, vx: speed * Math.sin(angle) || speed * 0.4, vy: -speed };
  }, []);

  const start = useCallback((lvl: number) => {
    const speed = BALL_SPEED_BY_LEVEL[Math.min(lvl - 1, BALL_SPEED_BY_LEVEL.length - 1)];
    g.current.bricks = buildBricks(lvl);
    g.current.paddleX = (W - PADDLE_W) / 2;
    g.current.score = 0;
    g.current.lives = 3;
    g.current.speed = speed;
    g.current.running = false;
    resetBall(speed);
    setScore(0);
    setLives(3);
    setOver(false);
    setCleared(false);
    setRunning(false);
    savedRef.current = false;
    setLvl(lvl);
    setLevel('breakout', lvl);
  }, [buildBricks, resetBall]);

  // Init once on mount.
  useEffect(() => {
    start(prog.level || 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const endGame = useCallback((won: boolean) => {
    g.current.running = false;
    setRunning(false);
    if (won) setCleared(true); else setOver(true);
  }, []);

  // Main animation loop.
  useEffect(() => {
    let raf = 0;
    const ctx = canvasRef.current?.getContext('2d');
    const loop = () => {
      const s = g.current;
      const c = ctx;
      if (c) {
        c.clearRect(0, 0, W, H);
        c.fillStyle = '#0f1226';
        c.fillRect(0, 0, W, H);

        if (s.running) {
          const b = s.ball;
          b.x += b.vx; b.y += b.vy;
          // Walls.
          if (b.x - BALL_R < 0) { b.x = BALL_R; b.vx *= -1; }
          if (b.x + BALL_R > W) { b.x = W - BALL_R; b.vx *= -1; }
          if (b.y - BALL_R < 0) { b.y = BALL_R; b.vy *= -1; }
          // Paddle.
          const py = H - 24;
          if (b.y + BALL_R >= py && b.y + BALL_R <= py + PADDLE_H && b.x >= s.paddleX && b.x <= s.paddleX + PADDLE_W && b.vy > 0) {
            const hit = (b.x - (s.paddleX + PADDLE_W / 2)) / (PADDLE_W / 2); // -1..1
            const ang = hit * (Math.PI / 3); // up to 60°
            b.vx = s.speed * Math.sin(ang);
            b.vy = -Math.abs(s.speed * Math.cos(ang));
          }
          // Floor → lose a life.
          if (b.y - BALL_R > H) {
            s.lives -= 1;
            setLives(s.lives);
            if (s.lives <= 0) { endGame(false); }
            else { s.running = false; setRunning(false); resetBall(s.speed); }
          }
          // Bricks.
          for (const brick of s.bricks) {
            if (!brick.alive) continue;
            if (b.x + BALL_R > brick.x && b.x - BALL_R < brick.x + brick.w &&
                b.y + BALL_R > brick.y && b.y - BALL_R < brick.y + brick.h) {
              brick.alive = false;
              s.score += 10 * level;
              setScore(s.score);
              sfx.pop();
              // Bounce vertically (simple).
              b.vy *= -1;
              break;
            }
          }
          if (s.bricks.every(br => !br.alive)) endGame(true);
        }

        // Draw bricks.
        for (const brick of s.bricks) {
          if (!brick.alive) continue;
          c.fillStyle = brick.color;
          c.fillRect(brick.x, brick.y, brick.w, brick.h);
        }
        // Paddle.
        c.fillStyle = '#e8eaed';
        c.fillRect(s.paddleX, H - 24, PADDLE_W, PADDLE_H);
        // Ball.
        c.fillStyle = '#ffd54f';
        c.beginPath();
        c.arc(s.ball.x, s.ball.y, BALL_R, 0, Math.PI * 2);
        c.fill();
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [level, endGame, resetBall]);

  // Persist on end.
  useEffect(() => {
    if ((over || cleared) && !savedRef.current) {
      savedRef.current = true;
      recordScore('breakout', g.current.score, level);
      if (cleared) { unlockNext('breakout', level); sfx.win(); } else sfx.lose();
    }
  }, [over, cleared, level]);

  const movePaddleTo = (clientX: number) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((clientX - rect.left) / rect.width) * W - PADDLE_W / 2;
    g.current.paddleX = Math.max(0, Math.min(W - PADDLE_W, x));
  };

  const launch = () => {
    if (over || cleared) return;
    g.current.running = true;
    setRunning(true);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const step = 26;
      if (e.key === 'ArrowLeft' || e.key === 'a') { g.current.paddleX = Math.max(0, g.current.paddleX - step); }
      else if (e.key === 'ArrowRight' || e.key === 'd') { g.current.paddleX = Math.min(W - PADDLE_W, g.current.paddleX + step); }
      else if (e.key === ' ') { e.preventDefault(); launch(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line
  }, [over, cleared]);

  return (
    <div className="gbreakout">
      <div className="game-toolbar">
        <button className="game-back" onClick={onExit}>← Arcade</button>
        <div className="game-stats">
          <span className="gstat"><b>{score}</b><i>score</i></span>
          <span className="gstat"><b>{Math.max(best, score)}</b><i>best</i></span>
          <span className="gstat"><b>{'●'.repeat(Math.max(0, lives)) || '—'}</b><i>lives</i></span>
        </div>
        <button className="game-restart" onClick={() => start(level)}>↻ New</button>
      </div>

      <div className="g2048-levels">
        {BRICK_ROWS_BY_LEVEL.map((_, i) => {
          const lvl = i + 1;
          const unlocked = lvl <= getProgress('breakout').unlockedLevel;
          return (
            <button key={lvl} className={`lvl-pill ${level === lvl ? 'active' : ''} ${unlocked ? '' : 'locked'}`}
              onClick={() => unlocked && start(lvl)} disabled={!unlocked}>
              {unlocked ? `Lvl ${lvl}` : '🔒'}
            </button>
          );
        })}
      </div>

      {challengeScore != null && (
        <div className="challenge-target">🎯 Beat the challenge: <b>{challengeScore}</b> points</div>
      )}

      <div className="gbreakout-wrap" style={{ width: W, maxWidth: '100%' }}>
        <canvas
          ref={canvasRef}
          width={W}
          height={H}
          className="gbreakout-canvas"
          onMouseMove={e => movePaddleTo(e.clientX)}
          onTouchMove={e => { if (e.touches[0]) movePaddleTo(e.touches[0].clientX); }}
          onClick={launch}
        />
        {!running && !over && !cleared && (
          <div className="gbreakout-hint" onClick={launch}>Move with mouse / finger · click or press Space to launch 🚀</div>
        )}
      </div>

      {(over || cleared) && (
        <div className="game-overlay">
          <div className="game-overlay-card">
            <div className="go-emoji">{cleared ? '🏆' : '💥'}</div>
            <h3>{cleared ? `Level ${level} cleared!` : 'Game over'}</h3>
            <p>You scored <b>{score}</b> points.{cleared && level < BRICK_ROWS_BY_LEVEL.length ? ' Next level unlocked.' : ''}</p>
            <div className="go-actions">
              {cleared && level < BRICK_ROWS_BY_LEVEL.length && (
                <button className="g-btn primary" onClick={() => start(level + 1)}>Next level →</button>
              )}
              <button className="g-btn" onClick={() => start(level)}>Play again</button>
              <button className="g-btn ghost" onClick={onExit}>Back to arcade</button>
            </div>
          </div>
        </div>
      )}

      <p className="game-hint">Move the paddle with mouse / finger or ← →. Don’t drop the ball.</p>
    </div>
  );
};

export default GameBreakout;
